import {
  Box,
  Button,
  Grid,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useLayout } from 'hooks/useLayout'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { ActivityButton } from 'components/ActivityButton'
import { colorPrimitives, Colors } from 'components/Theme'
import {
  formatCurrency,
  formatFullDate,
  pdfDownloadFromByteArray,
} from 'utils/util'
import { uniqBy } from 'lodash'
import { useGetDownloadFlexPayStatements } from 'hooks/api/Reports/useGetDownloadFlexPayStatements'
import { useSnackbar } from 'stores/useSnackbar'
import { Page } from 'components/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import {
  type FullLicensedEstablishmentFlexPayStatementDTOPageResult,
  type LicensedEstablishment,
} from 'src/types/api'
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  type GridSortItem,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid'
import { SelectFlexPayType } from 'components/SelectFlexPayType'
import { type FlexPayTypes } from 'src/types/flexPay'
import { DataTablePaging } from 'components/Shared/DataTablePaging'
import { useGetFlexPayStatements } from 'hooks/api/Reports/useGetFlexPayStatements'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enUS } from 'date-fns/locale'
import { isValid } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useSetContactUsFields } from 'stores/useSetContactUsFields'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { useTheme } from '@mui/material/styles'
import { hasContactUsPermission, getLEIdsByPermission } from 'utils/permissions'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useSetMe } from 'stores/useMeQuery'

const StyledDatePicker = styled(DatePicker)({
  '& .MuiFormLabel-root': {
    fontSize: '1rem',
    fontWeight: '100',
    color: Colors.muiLabels.placeholder,
  },
  '& .Mui-focused': {
    color: !Colors.muiLabels.placeholder,
  },
})

const getDefaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 30)
  date.setHours(0, 0, 0, 0) // set to midnight 30 days ago
  return date
}

const getDefaultEndDate = () => {
  const date = new Date()
  date.setHours(23, 59, 59, 999)
  return date
}

export const FlexPayPage = () => {
  const { isMobile } = useLayout()
  const navigate = useNavigate()
  const updateLocation = useSetContactUsFields(
    (state) => state.updateSelectedLocation
  )
  const flexPay = useSetContactUsFields((state) => state.flexPay)
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  })

  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    { field: 'date', sort: 'desc' },
  ])

  const theme = useTheme()
  const isMediumWindow = useMediaQuery(theme.breakpoints.down('md'))
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  const [activeId, setActiveId] = useState<number | null>(null)
  const [downloadedReports] = useState<number[]>([])

  const setDownloadedReportNumbers = (p: any) => {
    downloadedReports.push(p)
  }

  const setSnackbarMessage = useSnackbar((state) => state.setMessage)

  const [licensedEstablishmentId, setLicensedEstablishmentId] =
    useState<number>(-1)

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'flexPayPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered =
    meQuery.data?.licensedEstablishments?.filter((x) =>
      leIdsByPermission.includes(x.id)
    )

  const sortedLicensedEstablishments = licensedEstablishmentsFiltered?.sort(
    (a: LicensedEstablishment, b: LicensedEstablishment) => {
      return (a.standardName ?? '') > (b.standardName ?? '') ? 1 : -1
    }
  )

  const [type, setType] = useState<string>()

  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<Date>(getDefaultEndDate())

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize })
  }, [licensedEstablishmentId, type, startDate, endDate])

  // map column names to a field that can be searched in the backend
  const fieldMapping: Record<string, string> = {
    standardName: 'licensedEstablishment.standardName',
  }

  const reportQuery = useGetFlexPayStatements({
    licensedEstablishmentIds:
      licensedEstablishmentId === -1
        ? licensedEstablishmentsFiltered?.map((le) => le.id)
        : [licensedEstablishmentId],
    currentPage: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    type: type !== undefined ? (type as unknown as FlexPayTypes) : null,
    startDate,
    endDate,
    sortColumnName: fieldMapping[sortModel[0]?.field] || sortModel[0]?.field, // Convert field name
    sortOrder: sortModel[0]?.sort ?? 'desc',
    enabled: licensedEstablishmentId !== 0,
  })

  const dataRows = (reportQuery.data ??
    []) as FullLicensedEstablishmentFlexPayStatementDTOPageResult

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model)
  }

  // Handler to map GridSortModel to your expected type
  const handleSortModelChange = (model: GridSortItem[]) => {
    setSortModel(model)
  }

  const useGetDownloadFlexPayStatementsMutation =
    useGetDownloadFlexPayStatements({
      onSuccess: () => {},
      onError: () => {},
    })

  const handleDownload = async (params: any) => {
    setActiveId(params.id)

    useGetDownloadFlexPayStatementsMutation.mutate(
      {
        licensedEstablishmentId:
          params.row !== undefined
            ? params.row.licensedEstablishmentId
            : params.licensedEstablishmentId,
        license: params.row !== undefined ? params.row.license : params.license,
        type: params.row !== undefined ? params.row.type : params.type,
        blobName:
          params.row !== undefined ? params.row.blobName : params.blobName,
        blobDate: params.row !== undefined ? params.row.blobDate : params.date,
      },
      {
        onSuccess: (data) => {
          setDownloadedReportNumbers(params.id)

          pdfDownloadFromByteArray(
            data,
            params.row !== undefined ? params.row.blobName : params.blobName
          )
          setActiveId(null)
          setSnackbarMessage('Report successfully downloaded')
        },
        onError: (error) => {
          setActiveId(null)
          console.error('Error:', error)
          setSnackbarMessage(
            'There was an error downloading the Report',
            'error'
          )
        },
      }
    )
  }

  const handleDownloadSelected = async () => {
    let i = -1
    for (const location of selectedLocations) {
      i++
      const params = {
        id: location,
        licensedEstablishmentId: dataRows.results?.[i].licensedEstablishmentId,
        license: dataRows.results?.[i].license,
        type: dataRows.results?.[i].type,
        blobName: dataRows.results?.[i].blobName,
        blobDate: dataRows.results?.[i].date,
      }
      await new Promise((resolve) =>
        setTimeout(resolve, Math.floor(Math.random() * 5000))
      )
      await handleDownload(params)
    }
  }

  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const [justClickedEdit, setJustClickedEdit] = useState<boolean>(false)
  const handleOnChange = (newSelection: any) => {
    if (justClickedEdit) {
      setJustClickedEdit(false)
    } else {
      setSelectedLocations(newSelection)
    }
  }

  const columns: Array<GridColDef<any>> = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      flex: 0.2,
    },
    {
      field: 'standardName',
      headerName: 'Location',
      sortingOrder: ['asc', 'desc'],
      flex: 0.5,
      valueGetter: (params: {
        row: { licensedEstablishment: { standardName: string } }
      }) => params.row.licensedEstablishment.standardName,
    },
    {
      field: 'type',
      headerName: 'Type',
      sortingOrder: ['asc', 'desc'],
      flex: 0.3,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 0.275,
      sortingOrder: ['asc', 'desc'],
      valueGetter: (params: { row: { date: string } }) => params.row.date,
      valueFormatter: ({ value }: { value: string }) => formatFullDate(value),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      sortingOrder: ['asc', 'desc'],
      flex: isMediumWindow ? 0.3 : 0.35,
      valueGetter: (params: { row: { amount: number } }) => params.row.amount,
      valueFormatter: ({ value }: { value: number }) => formatCurrency(value),
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'download',
      headerName: '',
      sortable: false,
      width: isMediumWindow ? 20 : 150,
      align: 'center',
      renderCell: (params: { row: any }) => {
        const isReportDownloaded = !downloadedReports?.includes(params.row.id)
        return isReportDownloaded ? (
          <ActivityButton
            icon={<DownloadIcon />}
            sx={{
              border: `1px solid ${colorPrimitives.redGaming}`,
              background: 'white',
            }}
            active={
              useGetDownloadFlexPayStatementsMutation.isPending &&
              activeId === params.row.id
            }
            fullWidth
            variant="outlined"
            className="px-6 py-2 rounded"
            onClick={async () => await handleDownload(params)}
          >
            Download
          </ActivityButton>
        ) : (
          <ActivityButton
            icon={
              <CheckCircle
                sx={{
                  color: isReportDownloaded
                    ? colorPrimitives.redGaming
                    : colorPrimitives.successGreen,
                }}
              />
            }
            active={
              useGetDownloadFlexPayStatementsMutation.isPending &&
              activeId === params.row.id
            }
            color="success"
            variant="contained"
            onClick={async () => await handleDownload(params)}
          >
            Complete
          </ActivityButton>
        )
      },
    },
  ]

  const columnsMobile: Array<GridColDef<any>> = [
    {
      field: 'standardName',
      headerName: 'LE Name',
      flex: 0.7,
      valueGetter: (params: {
        row: { licensedEstablishment: { standardName: string } }
      }) => params.row.licensedEstablishment.standardName,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.5,
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 0.25,
      valueGetter: (params: { row: { date: string } }) => params.row.date,
      valueFormatter: ({ value }: { value: string }) => formatFullDate(value),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.2,
      valueGetter: (params: { row: { amount: number } }) => params.row.amount,
      valueFormatter: ({ value }: { value: number }) => formatCurrency(value),
      headerAlign: 'right',
      align: 'right',
    },
  ]

  // handle download button for mobile view only
  const handleDownloadButtonClicked = async (e: any) => {
    await handleDownload(e)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      <Page
        header={
          <PageHeader
            title="FlexPay"
            actionButton={
              <div hidden={!hasContactUsPermission()}>
                <Button
                  onClick={async () => {
                    if (licensedEstablishmentId !== -1) {
                      updateLocation(Number(licensedEstablishmentId))
                    }
                    flexPay()
                    await navigate('/ContactUs')
                  }}
                  variant="contained"
                >
                  Questions
                </Button>
              </div>
            }
          />
        }
      >
        <Grid container className="flex flex-column" rowGap={2} columnGap={3}>
          <FormControl>
            <Select
              displayEmpty
              onChange={(event) => {
                setLicensedEstablishmentId(Number(event.target.value))
              }}
              value={licensedEstablishmentId}
            >
              <MenuItem value={-1}>All locations</MenuItem>
              {uniqBy(
                sortedLicensedEstablishments?.filter(
                  (le) => le.corporateAccountId === selectedCorporateAccountId
                ),
                'id'
              )?.map((le) => {
                return (
                  <MenuItem key={le.id} value={le.id}>
                    {le.standardName} (#{le.licenseNumber})
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
          <SelectFlexPayType
            onSelect={setType}
            type={type}
            disabledValue={licensedEstablishmentId === 0}
          />
          <StyledDatePicker
            disabled={licensedEstablishmentId === 0}
            disableFuture
            onChange={(date: any) => {
              if (isValid(date)) {
                setStartDate(date.setHours(0, 0, 0, 0))
              }
            }}
            label="Start Date"
            value={startDate}
            format="MM/dd/yyyy"
          />

          <StyledDatePicker
            disabled={licensedEstablishmentId === 0}
            onChange={(date: any) => {
              if (isValid(date)) {
                setEndDate(date.setHours(23, 59, 59, 0))
              }
            }}
            label="End Date"
            value={endDate}
            format="MM/dd/yyyy"
          />
        </Grid>
        {startDate > endDate && (
          <ErrorIndicator
            color={colorPrimitives.darkRed}
            message={'Start Date must be before End Date'}
          />
        )}
        <Stack mt={3} spacing={4}>
          {reportQuery.isError && <ErrorIndicator />}

          {reportQuery.isPending && reportQuery.isFetching ? (
            <ActivityIndicator />
          ) : !reportQuery.data ? (
            <Typography mt={3} variant="h4">
              No data available for the selected location
            </Typography>
          ) : (
            <Box>
              {!isMobile ? (
                <Box>
                  <Typography variant="h3">Download Reports</Typography>
                  <Box className="flex lg:justify-end" sx={{ mb: 1 }}>
                    <Button
                      variant="contained"
                      onClick={async () => await handleDownloadSelected()}
                      disabled={selectedLocations.length === 0}
                    >
                      <Typography>Download Selected</Typography>
                    </Button>
                  </Box>
                </Box>
              ) : null}

              <DataTablePaging
                columns={isMobile ? columnsMobile : columns}
                rows={dataRows.results ?? []}
                totalCount={dataRows.rowCount ?? 0}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                checkboxSelection
                rowSelectionModel={selectedLocations}
                onRowSelectionModelChange={handleOnChange}
                mobileCustomButtonText="Download"
                onMobileCustomButtonClicked={handleDownloadButtonClicked}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
              />
            </Box>
          )}
        </Stack>
      </Page>
    </LocalizationProvider>
  )
}
